import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { Button } from "@unified-checkout/design-system";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useLangStore } from "@app/dashboard/store/lang";
import type { TDrawer } from "@app/dashboard/types/innerLayout/disputes";
import { respondToDispute } from "@app/dashboard/services/inner_layout/disputes/respond";
import { notify } from "@app/dashboard/utils/notifier";
import { DetailsBox } from "../../../components/DetailsBox";
import {
  NO_VALUE,
  TOASTER_SHARED_PROPS,
  formatValues,
  sharedActionBtnsProps
} from "../../../../utils";
import styles from "./accept-dispute.module.scss";


export const AcceptDispute = ({
  disputeId,
  reason,
  disputedAmount,
  currency,
  setIsDrawerOpen
}: {
  disputeId: string,
  reason?: string,
  disputedAmount: number,
  currency: string,
  setIsDrawerOpen: Dispatch<SetStateAction<Record<TDrawer, boolean>>>,
}) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const queryClient = useQueryClient();

  const {
    isPending,
    mutate
  } = useMutation({
    mutationKey: [QUERY_KEYS.RESPOND_TO_DISPUTE],
    mutationFn: () => respondToDispute({ disputeId, action: 'accept' }),
    onSuccess: () => {

      notify.success(t('innerLayout.disputesPage.drawers.accept.successMsg'), {
        ...TOASTER_SHARED_PROPS,
        onCloseHandler: () => {
          setIsDrawerOpen((prev) => ({ ...prev, accept: false }));
          queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_SINGLE_DISPUTE, lang] });
        }
      });
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const msg = (axiosErr?.response?.data as any)?.message || t('errors.somethingWentWrong');

      notify.error(msg, TOASTER_SHARED_PROPS);
    },
  });


  const ITEMS_LIST = [
    {
      label: t('innerLayout.disputesPage.drawers.accept.detailsBoxes.disputeDetails.fields.disputeId'),
      value: disputeId,
    },
    {
      label: t('innerLayout.disputesPage.drawers.accept.detailsBoxes.disputeDetails.fields.reason'),
      value: reason ?
        { text: <span className={styles["accept-dispute__dispute-details__value--danger"]}>{reason}</span> } as any
        :
        NO_VALUE,
    },
    {
      label: t('innerLayout.disputesPage.drawers.accept.detailsBoxes.disputeDetails.fields.disputedAmount'),
      value: typeof disputedAmount === 'number' ?
        formatValues(
          currency,
          disputedAmount,
          lang
        ) : NO_VALUE,
    },
  ];

  const actionBtnsProps = [
    {
      ...sharedActionBtnsProps.secondary,
      text: t('form.back'),
      disabled: isPending,
      onClickHandler: () => setIsDrawerOpen((prev) => ({ ...prev, accept: false }))
    },
    {
      ...sharedActionBtnsProps.main,
      text: t('innerLayout.disputesPage.actions.acceptDispute'),
      loading: isPending,
      onClickHandler: () => mutate()
    },
  ];


  return (
    <div className={styles["accept-dispute"]}>
      <div className={styles["accept-dispute__dispute-details"]}>
        <DetailsBox
          label={t('innerLayout.disputesPage.drawers.accept.detailsBoxes.disputeDetails.title')}
          itemsList={ITEMS_LIST}
        />
      </div>
      <div className="drawer-body__footer">
        {
          actionBtnsProps.map((btn, index) =>
            <Button {...btn} key={index} />
          )
        }
      </div>
    </div>
  )
}
